import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];
const companyId = () => Number(localStorage.getItem("companyId") || 0);

const val = (r, ...keys) => {
  for (const k of keys) {
    const x = r?.[k];
    if (x !== undefined && x !== null) return x;
  }
  return "";
};

const safe = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) =>
  num(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const errText = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data;

  if (typeof d === "string" && d.trim()) return d.trim();
  if (d?.message && String(d.message).trim()) return String(d.message).trim();
  if (d?.title && String(d.title).trim()) return String(d.title).trim();

  if (d?.errors && typeof d.errors === "object") {
    const firstError = Object.values(d.errors).flat().find(Boolean);
    if (firstError) return String(firstError);
  }

  if (e?.message && String(e.message).trim()) return String(e.message).trim();

  return fallback;
};

const styles = {
  page: { fontSize: 11, padding: 0 },

  card: {
    background: "#fff",
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
  },

  header: {
    background: "linear-gradient(90deg, rgb(11, 79, 74), rgb(15, 118, 110))",
    color: "#fff",
    padding: "9px 12px",
  },

  title: {
    fontSize: 13,
    fontWeight: 800,
    margin: 0,
  },

  sub: {
    fontSize: 10,
    opacity: 0.85,
  },

  body: {
    padding: 10,
  },

  label: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 3,
    color: "#495057",
  },

  input: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
  },

  btn: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 700,
  },

  section: {
    fontSize: 11,
    fontWeight: 800,
    color: "#0f766e",
    borderBottom: "1px solid #e9ecef",
    paddingBottom: 4,
    margin: "8px 0 6px",
  },

  tableWrap: {
    maxHeight: 390,
    overflow: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },

  table: {
    fontSize: 11,
    minWidth: 1200,
    marginBottom: 0,
  },

  th: {
    fontSize: 10,
    color: "#fff",
    background: "#07111f",
    padding: "7px 8px",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },

  td: {
    fontSize: 11,
    padding: "6px 8px",
    whiteSpace: "nowrap",
  },
};

export default function QuotationPage() {
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);

  const [form, setForm] = useState({
    quotationNo: "",
    quotationDate: today(),
    customerId: "",
    validTill: "",
    status: "OPEN",
    notes: "",
  });

  const [detail, setDetail] = useState({
    itemId: "",
    qty: 1,
    rate: 0,
    discountPercent: 0,
    gstPercent: 18,
    remarks: "",
  });

  const [details, setDetails] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const totals = useMemo(() => {
    const subTotal = details.reduce((s, x) => s + num(x.qty) * num(x.rate), 0);

    const discountAmount = details.reduce(
      (s, x) => s + (num(x.qty) * num(x.rate) * num(x.discountPercent)) / 100,
      0,
    );

    const taxAmount = details.reduce((s, x) => {
      const line = num(x.qty) * num(x.rate);
      const discount = (line * num(x.discountPercent)) / 100;
      return s + ((line - discount) * num(x.gstPercent)) / 100;
    }, 0);

    return {
      subTotal,
      discountAmount,
      taxAmount,
      grandTotal: subTotal - discountAmount + taxAmount,
    };
  }, [details]);

  useEffect(() => {
    initPage();
  }, []);

  const initPage = async () => {
    setLoading(true);
    setPageError("");

    await Promise.allSettled([
      loadMasters(false),
      loadQuotations(false),
      loadNextNo(),
    ]);

    setLoading(false);
  };

  const loadMasters = async (showError = true) => {
    const cid = companyId();

    try {
      const [c, i] = await Promise.all([
        client.get("/sales/customers", {
          params: cid ? { companyId: cid } : {},
        }),
        client.get("/inventory/items", {
          params: cid ? { companyId: cid } : {},
        }),
      ]);

      setCustomers(toArray(c.data));
      setItems(toArray(i.data));
    } catch (e) {
      console.error("Masters loading failed:", e);
      const msg = errText(e, "Failed to load customers/items");

      if (showError) {
        alert(msg);
      } else {
        setPageError((p) => p || msg);
      }
    }
  };

  const loadNextNo = async () => {
    try {
      const cid = companyId();

      const res = await client.get("/quotations/next-no", {
        params: cid ? { companyId: cid } : {},
      });

      const nextNo =
        safe(
          val(
            res.data,
            "QuotationNo",
            "quotationNo",
            "NextNo",
            "nextNo",
            "VoucherNo",
            "voucherNo",
          ),
        ) || `QT-${Date.now().toString().slice(-5)}`;

      setForm((f) => ({
        ...f,
        quotationNo: nextNo,
      }));
    } catch (e) {
      console.error("Next quotation no loading failed:", e);

      setForm((f) => ({
        ...f,
        quotationNo: f.quotationNo || `QT-${Date.now().toString().slice(-5)}`,
      }));
    }
  };

  const loadQuotations = async (showError = true) => {
    try {
      const cid = companyId();

      const res = await client.get("/quotations", {
        params: cid ? { companyId: cid } : {},
      });

      setRows(toArray(res.data));
    } catch (e) {
      console.error("Quotations loading failed:", e);
      const msg = errText(e, "Failed to load quotations");

      if (showError) {
        alert(msg);
      } else {
        setPageError((p) => p || msg);
      }
    }
  };

  const onItemChange = (itemId) => {
    const item = items.find(
      (x) => String(val(x, "Id", "id", "ItemId", "itemId")) === String(itemId),
    );

    setDetail((d) => ({
      ...d,
      itemId,
      rate: num(val(item, "SalePrice", "salePrice", "Rate", "rate")),
      gstPercent:
        num(val(item, "GSTPercent", "gstPercent", "GstPercent")) || 18,
    }));
  };

  const addDetail = () => {
    if (!detail.itemId) return alert("Select item");
    if (num(detail.qty) <= 0) return alert("Qty must be greater than zero");

    const item = items.find(
      (x) =>
        String(val(x, "Id", "id", "ItemId", "itemId")) ===
        String(detail.itemId),
    );

    const line = num(detail.qty) * num(detail.rate);
    const discount = (line * num(detail.discountPercent)) / 100;
    const tax = ((line - discount) * num(detail.gstPercent)) / 100;

    setDetails((old) => [
      ...old,
      {
        itemId: Number(detail.itemId),
        itemCode: safe(val(item, "ItemCode", "itemCode")),
        itemName: safe(val(item, "ItemName", "itemName", "Name", "name")),
        qty: num(detail.qty),
        rate: num(detail.rate),
        discountPercent: num(detail.discountPercent),
        gstPercent: num(detail.gstPercent),
        taxAmount: tax,
        lineTotal: line - discount + tax,
        remarks: safe(detail.remarks),
      },
    ]);

    setDetail({
      itemId: "",
      qty: 1,
      rate: 0,
      discountPercent: 0,
      gstPercent: 18,
      remarks: "",
    });
  };

  const removeDetail = (index) => {
    setDetails((old) => old.filter((_, i) => i !== index));
  };

  const resetForm = async () => {
    setForm({
      quotationNo: "",
      quotationDate: today(),
      customerId: "",
      validTill: "",
      status: "OPEN",
      notes: "",
    });

    setDetail({
      itemId: "",
      qty: 1,
      rate: 0,
      discountPercent: 0,
      gstPercent: 18,
      remarks: "",
    });

    setDetails([]);
    await loadNextNo();
  };

  const save = async () => {
    if (!form.quotationNo.trim()) return alert("Quotation No is required");
    if (!form.customerId) return alert("Customer is required");
    if (details.length === 0) return alert("Add at least one item");

    try {
      setSaving(true);

      await client.post("/quotations", {
        companyId: companyId(),
        quotationNo: form.quotationNo.trim(),
        quotationDate: form.quotationDate,
        customerId: Number(form.customerId),
        validTill: form.validTill || null,
        status: form.status,
        notes: form.notes,
        subTotal: totals.subTotal,
        discountAmount: totals.discountAmount,
        taxAmount: totals.taxAmount,
        grandTotal: totals.grandTotal,
        details,
      });

      alert("Quotation saved successfully");

      await resetForm();
      await loadQuotations(false);
    } catch (e) {
      console.error("Quotation save failed:", e);
      alert(errText(e, "Failed to save quotation"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-file-earmark-text me-2"></i>
            Quotation / Estimate
          </h6>
          <div style={styles.sub}>
            Professional customer estimate with GST, item grid, totals and
            status
          </div>
        </div>

        <div style={styles.body}>
          {loading && (
            <div
              className="alert alert-info py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              Loading quotation data...
            </div>
          )}

          {!loading && pageError && (
            <div
              className="alert alert-warning d-flex justify-content-between align-items-center py-2 mb-2"
              style={{ fontSize: 11 }}
            >
              <span>
                <b>Warning:</b> {pageError}
              </span>
              <button
                className="btn btn-sm btn-outline-dark"
                style={{ fontSize: 10 }}
                onClick={initPage}
              >
                Retry
              </button>
            </div>
          )}

          <div className="row g-2 mb-2">
            <Stat title="Sub Total" value={`₹ ${money(totals.subTotal)}`} />
            <Stat
              title="Discount"
              value={`₹ ${money(totals.discountAmount)}`}
            />
            <Stat title="GST" value={`₹ ${money(totals.taxAmount)}`} />
            <Stat title="Grand Total" value={`₹ ${money(totals.grandTotal)}`} />
          </div>

          <div style={styles.section}>Quotation Header</div>

          <div className="row g-2 mb-2">
            <Field label="Quotation No">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.quotationNo}
                onChange={(e) =>
                  setForm({ ...form, quotationNo: e.target.value })
                }
              />
            </Field>

            <Field label="Quotation Date">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.quotationDate}
                onChange={(e) =>
                  setForm({ ...form, quotationDate: e.target.value })
                }
              />
            </Field>

            <Field label="Customer" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.customerId}
                onChange={(e) =>
                  setForm({ ...form, customerId: e.target.value })
                }
              >
                <option value="">Select Customer</option>
                {customers.map((c, index) => {
                  const id = val(c, "Id", "id", "CustomerId", "customerId");
                  return (
                    <option key={id || index} value={id}>
                      {safe(
                        val(
                          c,
                          "CustomerName",
                          "customerName",
                          "Name",
                          "name",
                          "AccountName",
                          "accountName",
                        ),
                      )}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Valid Till">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.validTill}
                onChange={(e) =>
                  setForm({ ...form, validTill: e.target.value })
                }
              />
            </Field>

            <Field label="Status">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="OPEN">OPEN</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
                <option value="CONVERTED">CONVERTED</option>
              </select>
            </Field>

            <Field label="Notes" col="col-md-3">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>

          <div style={styles.section}>Item Details</div>

          <div
            className="row g-2 mb-2 p-2"
            style={{
              background: "#f8fafc",
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <Field label="Item" col="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={detail.itemId}
                onChange={(e) => onItemChange(e.target.value)}
              >
                <option value="">Select Item</option>
                {items.map((item, index) => {
                  const id = val(item, "Id", "id", "ItemId", "itemId");
                  return (
                    <option key={id || index} value={id}>
                      {safe(val(item, "ItemCode", "itemCode"))} -{" "}
                      {safe(val(item, "ItemName", "itemName", "Name", "name"))}
                    </option>
                  );
                })}
              </select>
            </Field>

            <Field label="Qty">
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.qty}
                onChange={(e) => setDetail({ ...detail, qty: e.target.value })}
              />
            </Field>

            <Field label="Rate">
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.rate}
                onChange={(e) => setDetail({ ...detail, rate: e.target.value })}
              />
            </Field>

            <Field label="Disc %">
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.discountPercent}
                onChange={(e) =>
                  setDetail({ ...detail, discountPercent: e.target.value })
                }
              />
            </Field>

            <Field label="GST %">
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.gstPercent}
                onChange={(e) =>
                  setDetail({ ...detail, gstPercent: e.target.value })
                }
              />
            </Field>

            <Field label="Remarks" col="col-md-2">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={detail.remarks}
                onChange={(e) =>
                  setDetail({ ...detail, remarks: e.target.value })
                }
              />
            </Field>

            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
                onClick={addDetail}
                disabled={loading}
              >
                Add
              </button>
            </div>
          </div>

          <div style={styles.tableWrap} className="mb-2">
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Code",
                    "Item",
                    "Qty",
                    "Rate",
                    "Disc %",
                    "GST %",
                    "Tax",
                    "Total",
                    "Remarks",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        [
                          "Qty",
                          "Rate",
                          "Disc %",
                          "GST %",
                          "Tax",
                          "Total",
                        ].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No items added
                    </td>
                  </tr>
                ) : (
                  details.map((d, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{d.itemCode}</td>
                      <td style={styles.td}>{d.itemName}</td>
                      <td className="text-end" style={styles.td}>
                        {money(d.qty)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.rate)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.discountPercent)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.gstPercent)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(d.taxAmount)}
                      </td>
                      <td className="text-end fw-bold" style={styles.td}>
                        {money(d.lineTotal)}
                      </td>
                      <td style={styles.td}>{d.remarks || "-"}</td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          style={{ fontSize: 10 }}
                          onClick={() => removeDetail(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end gap-2 mb-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ ...styles.btn, minWidth: 100 }}
              onClick={() => window.print()}
            >
              Print
            </button>

            <button
              className="btn btn-outline-dark btn-sm"
              style={{ ...styles.btn, minWidth: 100 }}
              onClick={resetForm}
              disabled={saving}
            >
              Clear
            </button>

            <button
              className="btn btn-success btn-sm"
              style={{ ...styles.btn, minWidth: 140 }}
              onClick={save}
              disabled={saving || loading}
            >
              {saving ? "Saving..." : "Save Quotation"}
            </button>
          </div>

          <div style={styles.section}>Recent Quotations</div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Quotation No",
                    "Date",
                    "Customer",
                    "Valid Till",
                    "Total",
                    "Status",
                    "Notes",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={h === "Total" ? "text-end" : ""}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      {loading ? "Loading..." : "No quotations found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => (
                    <tr
                      key={
                        val(r, "Id", "id", "QuotationId", "quotationId") ||
                        index
                      }
                    >
                      <td style={{ ...styles.td, fontWeight: 800 }}>
                        {safe(val(r, "QuotationNo", "quotationNo"))}
                      </td>
                      <td style={styles.td}>
                        {String(
                          val(r, "QuotationDate", "quotationDate") || "",
                        ).slice(0, 10)}
                      </td>
                      <td style={styles.td}>
                        {safe(
                          val(
                            r,
                            "CustomerName",
                            "customerName",
                            "Name",
                            "name",
                          ),
                        )}
                      </td>
                      <td style={styles.td}>
                        {String(val(r, "ValidTill", "validTill") || "").slice(
                          0,
                          10,
                        ) || "-"}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {money(val(r, "GrandTotal", "grandTotal"))}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "Status", "status")) || "-"}
                      </td>
                      <td style={styles.td}>
                        {safe(val(r, "Notes", "notes")) || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, col = "col-md-2" }) {
  return (
    <div className={col}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="col-md-3">
      <div
        style={{
          border: "1px solid #e9ecef",
          background: "#f8fafc",
          borderRadius: 10,
          padding: 9,
          borderTop: "3px solid #0f766e",
        }}
      >
        <div
          style={{
            fontSize: 10,
            color: "#64748b",
            fontWeight: 800,
          }}
        >
          {title}
        </div>

        <div
          style={{
            fontSize: 15,
            color: "#0f766e",
            fontWeight: 900,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}
